import { MATCH_STATUSES, type MatchEventKind, type MatchStatus } from '@mogzo/shared';
import { Badge } from '@mogzo/ui';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { Button, cardClass, ErrorNotice, fieldClass, Loading } from './ui';

type Env = 'test' | 'prod';

interface Person {
  playerId: string;
  /** Null in live, where players are anonymous ids. */
  name: string | null;
}
interface Turn extends Person {
  endsAt: string;
}
interface ListedMatch {
  id: string;
  mode: string;
  modeName: string;
  status: MatchStatus;
  how: 'quick' | 'invite';
  code: string | null;
  players: (Person & { place: number | null })[];
  turn: Turn | null;
  moves: number;
  lastMoveAt: string | null;
  createdAt: string;
}
interface MatchList {
  summary: { playing: number; waiting: number; endedThisWeek: number; disagreements: number };
  modes: { id: string; name: string }[];
  total: number;
  matches: ListedMatch[];
}
interface MatchDetail {
  match: {
    id: string;
    modeName: string;
    kind: string | null;
    status: MatchStatus;
    how: 'quick' | 'invite';
    code: string | null;
    turn: Turn | null;
    turnSeconds: number | null;
    missedTurn: 'skip' | 'forfeit' | null;
    moves: number;
    disagreements: number;
    state?: unknown;
    stateBytes: number;
    stateLimit: number;
    createdAt: string;
    startedAt: string | null;
    endedAt: string | null;
  };
  players: (Person & {
    seat: number | null;
    host: boolean;
    left: boolean;
    place: number | null;
    score: number | null;
    rating: number | null;
  })[];
  events: {
    seq: number;
    kind: MatchEventKind;
    player: Person | null;
    move?: unknown;
    at: string;
  }[];
  eventCount: number;
}

const STATUS_NAMES: Record<MatchStatus, string> = {
  waiting: 'Waiting',
  playing: 'Playing',
  ended: 'Ended',
  cancelled: 'Called off',
};

const who = (person: Person | null) =>
  person ? (person.name ?? `Player ${person.playerId.slice(0, 10)}`) : 'Someone';

/** `5 h left`, `0:24 left`, `2 days left`: how long the turn has. */
function timeLeft(endsAt: string, now = Date.now()) {
  const seconds = Math.max(0, Math.round((new Date(endsAt).getTime() - now) / 1000));
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} left`;
  if (seconds < 2 * 86400) return `${Math.round(seconds / 3600)} h left`;
  return `${Math.round(seconds / 86400)} days left`;
}

function duration(seconds: number) {
  if (seconds % 86400 === 0) return seconds === 86400 ? 'a day' : `${seconds / 86400} days`;
  if (seconds % 3600 === 0) return seconds === 3600 ? 'an hour' : `${seconds / 3600} hours`;
  if (seconds % 60 === 0) return seconds === 60 ? 'a minute' : `${seconds / 60} minutes`;
  return `${seconds} seconds`;
}

/** The status chip: whose turn and for how long, waiting with its code, or how it ended. */
function StatusChip({
  match,
}: {
  match: Pick<ListedMatch, 'status' | 'turn' | 'code' | 'players'>;
}) {
  const chip = (tone: 'info' | 'warning' | 'success' | 'neutral', text: string) => (
    <Badge tone={tone}>{text}</Badge>
  );
  if (match.status === 'playing') {
    return match.turn
      ? chip('info', `${who(match.turn)}’s turn · ${timeLeft(match.turn.endsAt)}`)
      : chip('info', 'Playing');
  }
  if (match.status === 'waiting') {
    return chip(
      'warning',
      match.code ? `Waiting for players · code ${match.code}` : 'Waiting for players',
    );
  }
  if (match.status === 'cancelled') return chip('neutral', 'Called off');
  const winners = match.players.filter((p) => p.place === 1);
  if (winners.length === 1) return chip('success', `Ended · ${who(winners[0] ?? null)} won`);
  return chip(
    winners.length > 1 ? 'success' : 'neutral',
    winners.length > 1 ? 'Ended · a draw' : 'Ended',
  );
}

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

/** One match: its players and ratings, state and moves (screen 2 of the approved mockup). */
function MatchPage({
  project,
  env,
  id,
  onBack,
}: {
  project: Project;
  env: Env;
  id: string;
  onBack: () => void;
}) {
  const [all, setAll] = useState(false);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const detail = useApi<MatchDetail>(
    `/v1/dev/projects/${project.id}/matches/${id}?env=${env}${all ? '&all=1' : ''}`,
    reload,
  );

  async function callOff() {
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', `/v1/dev/projects/${project.id}/matches/${id}/cancel`);
      setConfirming(false);
      setReload((n) => n + 1);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex min-h-8 items-center gap-1.5 self-start border-0 bg-transparent p-0 font-medium text-muted"
    >
      <ChevronLeft size={16} aria-hidden="true" />
      All matches
    </button>
  );
  if (detail.status === 'loading') return <Loading />;
  if (detail.status === 'error') {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <ErrorNotice error={detail.error} />
      </div>
    );
  }
  const { match, players, events, eventCount } = detail.data;
  const going = match.status === 'waiting' || match.status === 'playing';
  const facts = [
    match.kind === 'turns'
      ? 'Turns'
      : match.kind === 'realtime'
        ? 'Realtime'
        : match.kind === 'bases'
          ? 'Bases'
          : null,
    match.how === 'quick' ? 'quick match' : 'invite code',
    match.startedAt ? `started ${timeAgo(match.startedAt)}` : `made ${timeAgo(match.createdAt)}`,
    `${match.moves} ${match.moves === 1 ? 'move' : 'moves'}`,
    match.turnSeconds
      ? `${duration(match.turnSeconds)} for a turn, ${match.missedTurn === 'forfeit' ? 'lost' : 'skipped'} when missed`
      : null,
    match.disagreements ? `${match.disagreements} results that disagreed` : null,
  ].filter(Boolean);
  const hidden = eventCount - events.length;
  const eventText = (e: MatchDetail['events'][number]) =>
    e.kind === 'skipped'
      ? `${who(e.player)} ran out of time: turn skipped`
      : e.kind === 'forfeited'
        ? `${who(e.player)} ran out of time and lost the match`
        : e.kind === 'resigned'
          ? `${who(e.player)} resigned`
          : null;

  return (
    <div className="flex flex-col gap-4">
      {back}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2.5 text-xl font-semibold">
            {match.modeName}
            <StatusChip match={{ ...match, players }} />
          </h2>
          <p className="mt-0.5 text-[13px] text-muted">
            {facts.join(' · ')} · <span className="font-mono">{match.id.slice(0, 8)}</span>
          </p>
        </div>
        {env === 'test' && going && !confirming && (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Call off the match
          </Button>
        )}
      </div>
      {confirming && (
        <div className="flex flex-col gap-2.5 rounded-[6px] bg-danger-soft px-4 py-3.5">
          <strong className="text-danger">Call off this test match?</strong>
          <p className="text-muted">
            It ends without results: nobody’s rating changes, and the players’ games hear it’s over.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={callOff}
              disabled={busy}
              className="mz-btn mz-btn-danger"
            >
              Yes, call it off
            </button>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Keep playing
            </Button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}

      <section
        aria-labelledby="players-heading"
        className={`${cardClass} flex flex-col gap-2.5 px-5 py-4`}
      >
        <h3 id="players-heading" className="text-[15px] font-semibold">
          {match.kind === 'turns' ? 'Players, in turn order' : 'Players'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                {match.kind === 'turns' && (
                  <th className="border-b border-divider px-2.5 py-2">Seat</th>
                )}
                <th className="border-b border-divider px-2.5 py-2">Player</th>
                <th className="border-b border-divider px-2.5 py-2">Now</th>
                <th className="border-b border-divider px-2.5 py-2">Place</th>
                <th className="border-b border-divider px-2.5 py-2 text-right">Score</th>
                <th className="border-b border-divider px-2.5 py-2 text-right">
                  Rating in this mode
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.playerId}>
                  {match.kind === 'turns' && (
                    <td className="border-b border-divider px-2.5 py-2.5">{(p.seat ?? 0) + 1}</td>
                  )}
                  <td className="border-b border-divider px-2.5 py-2.5">
                    <b className="font-semibold">{who(p)}</b>{' '}
                    <span className="font-mono text-xs text-muted">{p.playerId.slice(0, 10)}</span>
                    {p.host ? ' · host' : ''}
                  </td>
                  <td className="border-b border-divider px-2.5 py-2.5">
                    {p.left ? (
                      <span className="text-muted">Left</span>
                    ) : match.turn?.playerId === p.playerId ? (
                      <Badge tone="info">Their turn</Badge>
                    ) : (
                      <span className="text-muted">
                        {match.status === 'playing' ? 'Waiting' : '–'}
                      </span>
                    )}
                  </td>
                  <td className="border-b border-divider px-2.5 py-2.5">{p.place ?? '–'}</td>
                  <td className="border-b border-divider px-2.5 py-2.5 text-right tabular-nums">
                    {p.score?.toLocaleString('en') ?? '–'}
                  </td>
                  <td className="border-b border-divider px-2.5 py-2.5 text-right tabular-nums">
                    {p.rating === null ? '–' : Math.round(p.rating).toLocaleString('en')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[13px] text-muted">
          Players never see ratings; quick match uses them to pick opponents.
        </p>
      </section>

      {match.kind === 'turns' && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4">
          <section
            aria-labelledby="state-heading"
            className={`${cardClass} flex min-w-0 flex-col gap-2.5 px-5 py-4`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 id="state-heading" className="text-[15px] font-semibold">
                State
              </h3>
              <span className="text-xs text-muted">
                {match.stateBytes.toLocaleString('en')} bytes of {match.stateLimit / 1024} KB
                {match.moves ? ` · after move ${match.moves}` : ''}
              </span>
            </div>
            <pre className="max-h-[420px] overflow-auto rounded-[8px] bg-field px-3.5 py-3 font-mono text-[13px] leading-5 whitespace-pre-wrap">
              {match.state === null || match.state === undefined
                ? 'No state yet: the first move sends it.'
                : JSON.stringify(match.state, null, 2)}
            </pre>
            <p className="text-[13px] text-muted">
              What the game sent with its last move. MOGZO keeps it; the game decides what it means.
            </p>
          </section>

          <section
            aria-labelledby="moves-heading"
            className={`${cardClass} flex min-w-0 flex-col gap-2.5 px-5 py-4`}
          >
            <h3 id="moves-heading" className="text-[15px] font-semibold">
              Moves
            </h3>
            {events.length === 0 ? (
              <p className="text-sm text-muted">No moves yet.</p>
            ) : (
              <ol className="flex max-h-[420px] flex-col overflow-auto">
                {events.map((e) => (
                  <li
                    key={e.seq}
                    className={`flex items-baseline gap-3 border-b border-divider py-2.5 ${e.kind === 'move' ? '' : 'text-muted'}`}
                  >
                    <b className="w-7 font-semibold text-muted">
                      {e.kind === 'move' ? e.seq : '–'}
                    </b>
                    <span className="min-w-0 flex-1 break-words">
                      {eventText(e) ?? (
                        <>
                          <b className="font-semibold text-ink">{who(e.player)}</b>{' '}
                          <span className="font-mono text-[13px]">{JSON.stringify(e.move)}</span>
                        </>
                      )}
                    </span>
                    <span className="text-[13px] text-muted">{timeAgo(e.at)}</span>
                  </li>
                ))}
              </ol>
            )}
            {hidden > 0 && !all && (
              <div className="flex items-center justify-between gap-3 text-muted">
                <span>{hidden === 1 ? '1 earlier move' : `${hidden} earlier moves`}</span>
                <Button variant="secondary" onClick={() => setAll(true)}>
                  Show all
                </Button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/** Matches in the data browser (M32): test and live, newest first (screen 1 of the mockup). */
export function Matches({ project, env }: { project: Project; env: Env }) {
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState<MatchStatus | ''>('');
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  // A search runs once typing stops.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(typed.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [typed]);

  const query = new URLSearchParams({ env, offset: String(offset) });
  if (mode) query.set('mode', mode);
  if (status) query.set('status', status);
  if (search) query.set('search', search);
  const list = useApi<MatchList>(`/v1/dev/projects/${project.id}/matches?${query}`);

  if (open) return <MatchPage project={project} env={env} id={open} onBack={() => setOpen(null)} />;

  const summary = list.status === 'ready' ? list.data.summary : null;
  const labelClass = 'flex flex-col gap-1 text-[13px] font-semibold text-muted';
  return (
    <div className="flex flex-col gap-4">
      {summary && (
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
          {(
            [
              ['Playing now', summary.playing],
              ['Waiting for players', summary.waiting],
              ['Ended this week', summary.endedThisWeek],
              ['Results that disagreed', summary.disagreements],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="mz-panel px-4 py-3">
              <dt className="text-[13px] text-muted">{label}</dt>
              <dd className="mz-text-title-lg m-0 mt-1 tabular-nums">
                {value.toLocaleString('en')}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Matches</h2>
          <p className="mt-0.5 text-[13px] text-muted">
            Every match in {env === 'test' ? 'test' : 'live'}, newest first. Open one for its
            players, moves and state.
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <label className={labelClass}>
            Mode
            <select
              value={mode}
              onChange={(event) => {
                setMode(event.target.value);
                setOffset(0);
              }}
              className={`${fieldClass} min-h-10`}
            >
              <option value="">All modes</option>
              {list.status === 'ready' &&
                list.data.modes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </select>
          </label>
          <label className={labelClass}>
            Status
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as MatchStatus | '');
                setOffset(0);
              }}
              className={`${fieldClass} min-h-10`}
            >
              <option value="">Any</option>
              {MATCH_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_NAMES[s]}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Player
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={env === 'test' ? 'Name or player id' : 'Player id'}
              className={`${fieldClass} min-h-10`}
            />
          </label>
        </div>
      </div>

      {list.status === 'loading' && <Loading />}
      {list.status === 'error' && <ErrorNotice error={list.error} />}
      {list.status === 'ready' &&
        (list.data.matches.length === 0 ? (
          <p className={`${cardClass} px-5 py-6 text-sm text-muted`}>
            {search || mode || status
              ? 'No matches like that.'
              : env === 'test'
                ? 'No matches yet. They show here as soon as testers start one.'
                : 'No live matches yet.'}
          </p>
        ) : (
          <div className={`${cardClass} overflow-x-auto`}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="border-b border-divider px-3.5 py-2.5">Mode</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Players</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Status</th>
                  <th className="border-b border-divider px-3.5 py-2.5 text-right">Moves</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Last move</th>
                  <th className="border-b border-divider px-3.5 py-2.5">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.matches.map((m) => (
                  <tr key={m.id}>
                    <td className="border-b border-divider px-3.5 py-3 font-semibold">
                      {m.modeName}
                    </td>
                    <td className="border-b border-divider px-3.5 py-3">
                      {m.players.map((p) => who(p)).join(', ')}
                    </td>
                    <td className="border-b border-divider px-3.5 py-3">
                      <StatusChip match={m} />
                    </td>
                    <td className="border-b border-divider px-3.5 py-3 text-right tabular-nums">
                      {m.moves}
                    </td>
                    <td className="border-b border-divider px-3.5 py-3 text-muted">
                      {m.lastMoveAt ? timeAgo(m.lastMoveAt) : '–'}
                    </td>
                    <td className="border-b border-divider px-3.5 py-2 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => setOpen(m.id)}
                        aria-label={`Open the ${m.modeName} match with ${m.players.map((p) => who(p)).join(', ')}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {list.status === 'ready' && list.data.total > 50 && (
        <div className="flex items-center justify-between gap-3 text-sm text-muted">
          <span>
            {offset + 1}–{Math.min(offset + 50, list.data.total)} of {list.data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(offset - 50)}
            >
              Newer
            </Button>
            <Button
              variant="secondary"
              disabled={offset + 50 >= list.data.total}
              onClick={() => setOffset(offset + 50)}
            >
              Older
            </Button>
          </div>
        </div>
      )}
      <p className="text-[13px] text-muted">
        {env === 'test'
          ? 'In live, players show as anonymous ids and matches can’t be changed.'
          : 'Players show as anonymous ids. A live match can’t be changed here.'}
      </p>
    </div>
  );
}
